import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Tv, Youtube, Star, ScrollText, ArrowRight, Pin } from "lucide-react";
import api from "../lib/api";
import { statusLabel } from "../constants/games";

const TWITCH_CHANNEL = "orchidnocturne";

export default function Adventures() {
  const [twitch, setTwitch] = useState(null);
  const [videos, setVideos] = useState([]);
  const [games, setGames] = useState([]);
  const [notices, setNotices] = useState([]);

  useEffect(() => {
    api.get("/twitch/status").then(r=>setTwitch(r.data)).catch(()=>{});
    api.get("/youtube/latest").then(r=>setVideos(r.data.videos || [])).catch(()=>{});
    api.get("/games").then(r=>setGames(r.data)).catch(()=>{});
    Promise.all([
      api.get("/posts", {params:{category:"notice-board"}}),
      api.get("/posts", {params:{category:"adventures"}}),
    ]).then(([a,b]) => {
      const merged = [...a.data, ...b.data]
        .sort((x,y) => new Date(y.created_at) - new Date(x.created_at))
        .slice(0, 4);
      setNotices(merged);
    }).catch(()=>{});
  }, []);

  return (
    <div className="page-container fade-in" data-testid="adventures-page">
      <div className="page-eyebrow">games & streams</div>
      <h1 className="page-title">Adventures</h1>
      <p className="page-sub">The quests of the moment, the next horizons, and the heroes (and disasters) gathered around the stream fire.</p>

      {/* TWITCH CHANNEL CARD */}
      <div className="parchment-card mt-10 p-6 md:p-8" data-testid="twitch-section">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-start gap-4">
            <div className="h-16 w-16 grid place-items-center rounded border flex-shrink-0" style={{borderColor:"var(--gold)", background:"rgba(0,0,0,0.3)"}}>
              <Tv className="h-7 w-7" style={{color:"var(--gold)"}} />
            </div>
            <div>
              <div className="page-eyebrow">the tavern hearth</div>
              <h2 className="tavern-heading text-2xl gold-text mt-1">twitch.tv/{TWITCH_CHANNEL}</h2>
              <div className="flex items-center gap-2 mt-2">
                {twitch?.is_live ? (
                  <>
                    <span className="live-dot" />
                    <span className="text-sm" style={{color:"var(--gold)"}}>LIVE NOW</span>
                    {twitch.game && <span className="text-sm" style={{color:"var(--muted)"}}>· {twitch.game}</span>}
                    {twitch.uptime && <span className="text-sm" style={{color:"var(--muted)"}}>· {twitch.uptime}</span>}
                  </>
                ) : (
                  <>
                    <span className="h-2 w-2 rounded-full" style={{background:"var(--muted)"}}/>
                    <span className="text-sm" style={{color:"var(--muted)"}}>The hearth is quiet — stream offline</span>
                  </>
                )}
              </div>
              {twitch?.title && <p className="text-sm mt-2" style={{color:"var(--silver)"}}>{twitch.title}</p>}
            </div>
          </div>
          <a className="gold-btn glow-pulse" href={`https://www.twitch.tv/${TWITCH_CHANNEL}`} target="_blank" rel="noreferrer" data-testid="open-twitch">
            <Tv className="h-4 w-4" /> Watch on Twitch
          </a>
        </div>

        <p className="text-sm mt-5 leading-relaxed" style={{color:"var(--muted)"}}>
          The tavern keeps flexible hours. Watch the Notice Board for upcoming adventures, project updates, and news from
          around the hearth. Some weeks are bustling, some are quiet, but there is always another story waiting around the corner.
        </p>
      </div>

      {/* NOTICE BOARD PREVIEW + GAMES */}
      <div className="grid lg:grid-cols-2 gap-6 mt-8">
        <div className="parchment-card p-6 md:p-8" data-testid="notice-preview-section">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <ScrollText className="h-5 w-5" style={{color:"var(--gold)"}} />
              <h2 className="tavern-heading text-2xl gold-text">From the Notice Board</h2>
            </div>
            <Link to="/notice-board" className="text-sm flex items-center gap-1 group" style={{color:"var(--orchid-light)"}} data-testid="notice-board-link">
              <span className="tavern-script text-lg">read the full board</span>
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>

          <ul className="mt-5 space-y-4">
            {notices.map((n) => (
              <li key={n.id} className="relative pl-6" data-testid={`adv-notice-${n.id}`}>
                <Pin className="absolute left-0 top-1 h-4 w-4" style={{color:"var(--gold)"}}/>
                <div className="tavern-heading text-lg" style={{color:"var(--silver)"}}>{n.title}</div>
                <div className="text-xs uppercase tracking-widest mt-0.5" style={{color:"var(--orchid-light)"}}>
                  {n.category.replace("-"," ")} · {new Date(n.created_at).toLocaleDateString()}
                </div>
                <p className="text-sm mt-1" style={{color:"var(--muted)"}}>{n.excerpt}</p>
              </li>
            ))}
            {!notices.length && (
              <li className="text-sm" style={{color:"var(--muted)"}}>
                The board is quiet today. New notices will be pinned here as adventures unfold.
              </li>
            )}
          </ul>
        </div>

        {/* CURRENT GAMES */}
        <div className="parchment-card p-6 md:p-8" data-testid="games-section">
          <div className="flex items-center gap-2">
            <Star className="h-5 w-5" style={{color:"var(--gold)"}} />
            <h2 className="tavern-heading text-2xl gold-text">Games We Play</h2>
          </div>
          <ul className="mt-5 space-y-3">
            {games.map(g => (
              <li key={g.id} className="flex items-center justify-between gap-3 p-3 rounded-sm" style={{background:"rgba(0,0,0,0.18)"}}>
                <div className="flex items-center gap-3 min-w-0">
                  {g.image_url && (
                    <img src={g.image_url} alt={g.name} className="w-14 h-14 object-cover rounded-sm flex-shrink-0"
                      style={{border:"1px solid rgba(212,175,55,0.2)"}} />
                  )}
                  <div className="min-w-0">
                    <div className="tavern-heading text-lg" style={{color:"var(--silver)"}}>{g.name}</div>
                    <div className="text-xs uppercase tracking-widest" style={{color:"var(--muted)"}}>
                      {g.genre ? `${g.genre} · ` : ""}{statusLabel(g.status)}
                    </div>
                    {g.review && <div className="text-sm mt-1" style={{color:"var(--muted)"}}>{g.review}</div>}
                  </div>
                </div>
                {g.rating && (
                  <div className="flex flex-shrink-0" style={{color:"var(--gold)"}}>
                    {Array.from({length:g.rating}).map((_,i)=><Star key={i} className="h-4 w-4 fill-current"/>)}
                  </div>
                )}
              </li>
            ))}
            {!games.length && (
              <li className="text-sm" style={{color:"var(--muted)"}}>The shelf is empty for now — new adventures will appear here soon.</li>
            )}
          </ul>
        </div>
      </div>

      {/* YOUTUBE */}
      <div className="parchment-card p-6 md:p-8 mt-8" data-testid="youtube-section">
        <div className="flex items-center gap-2">
          <Youtube className="h-5 w-5" style={{color:"var(--gold)"}} />
          <h2 className="tavern-heading text-2xl gold-text">Recent Recordings</h2>
        </div>
        <p className="text-sm mt-1" style={{color:"var(--muted)"}}>Latest videos from the YouTube archive.</p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-5">
          {videos.map(v => (
            <a key={v.video_id} href={v.url} target="_blank" rel="noreferrer" className="room-card block overflow-hidden" data-testid={`yt-${v.video_id}`}>
              {v.thumbnail && <img src={v.thumbnail} alt={v.title} className="w-full aspect-video object-cover" />}
              <div className="p-3">
                <div className="tavern-heading text-base gold-text leading-snug">{v.title}</div>
                <div className="text-xs mt-1" style={{color:"var(--muted)"}}>{new Date(v.published).toLocaleDateString()}</div>
              </div>
            </a>
          ))}
          {!videos.length && (
            <div className="col-span-full text-sm" style={{color:"var(--muted)"}}>
              The recordings are loading from the archive…
              <a className="ml-2 underline" href="https://www.youtube.com/@NocturneOrchid" target="_blank" rel="noreferrer">Visit the channel</a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
